import { CreatableMessage, UpdatableMessage } from 'ds/MessageThread/types';
import { BuildingUser, EditableImage, EditablePdf, Message, WorkOrder } from 'shared';
import axios from 'shared/lib/axios';

import { CreatableSupportTicket, SupportTicket, TenantEmployeeRequest, UpdatableSupportTicket } from './types';

export function getCustomerSupportTickets() {
  return axios.get<SupportTicket[]>('/v1/support_tickets');
}

export async function updateSupportTicket({
  supportTicket: { id, title, description, status, images = [] }
}: {
  supportTicket: UpdatableSupportTicket;
}) {
  const newImages = images.filter(i => !!i.file);
  const imagesToDestroy: { id: number; _destroy: boolean }[] = [];

  images.forEach(({ id, _destroy }) => {
    if (id && _destroy) {
      imagesToDestroy.push({ id, _destroy });
    }
  });

  if (newImages.length) {
    await uploadImages({ images: newImages, supportTicketId: id });
  }

  return axios.put<SupportTicket>(`/v1/support_tickets/${id}`, {
    title,
    id,
    description,
    status,
    images_attachments_attributes: imagesToDestroy
  });
}

export async function createSupportTicket({ supportTicket }: { supportTicket: CreatableSupportTicket }) {
  return axios.post<SupportTicket>('/v1/support_tickets', supportTicket);
}

function uploadImages({ images, supportTicketId }: { images: EditableImage[]; supportTicketId: number }) {
  const formData = new FormData();
  images.forEach(i => {
    if (i.file) {
      formData.append('images[]', i.file);
    }
  });
  return axios.patch<SupportTicket>(`/v1/support_tickets/${supportTicketId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
}

export async function createOrUpdateSupportTicketView({ supportTicketId }: { supportTicketId: number }) {
  return axios.post<SupportTicket>('/v1/support_ticket_views', { support_ticket_id: supportTicketId });
}

export const getSupportTicketMessages = async ({ supportTicketId }: { supportTicketId: number }) => {
  return axios.get<SupportTicket>(`/v1/support_tickets/${supportTicketId}/messages`);
};

export async function updateSupportTicketMessage({
  supportTicketId,
  message: { id, images = [], body }
}: {
  supportTicketId: number;
  message: UpdatableMessage;
}) {
  const newImages = images.filter(i => !!i.file);
  const imagesToDestroy: { id: number; _destroy: boolean }[] = [];

  images.forEach(({ id, _destroy }) => {
    if (id && _destroy) {
      imagesToDestroy.push({ id, _destroy });
    }
  });

  if (newImages.length) {
    await uploadSupportTicketMessageImages({ images: newImages, supportTicketId, messageId: id });
  }

  return axios.put<SupportTicket>(`/v1/support_tickets/${supportTicketId}/messages`, {
    body
  });
}

export async function createSupportTicketMessage({
  supportTicketId,
  message: { images = [], files = [], body }
}: {
  supportTicketId: number;
  message: CreatableMessage;
}) {
  const newImages = images.filter(i => !!i.file);
  const newFiles = files.filter(f => !!f.file);

  let response = await axios.post<Message>(`/v1/support_tickets/${supportTicketId}/messages`, {
    body
  });

  if (newImages.length) {
    response = await uploadSupportTicketMessageImages({
      images: newImages,
      messageId: response.data.id,
      supportTicketId
    });
  }

  if (newFiles.length) {
    response = await uploadSupportTicketMessageFiles({
      files: newFiles,
      messageId: response.data.id,
      supportTicketId
    });
  }

  return response;
}

function uploadSupportTicketMessageImages({
  images,
  supportTicketId,
  messageId
}: {
  images: EditableImage[];
  supportTicketId: number;
  messageId: number;
}) {
  const formData = new FormData();
  images.forEach(i => {
    if (i.file) {
      formData.append('images[]', i.file);
    }
  });
  return axios.patch<Message>(`/v1/support_tickets/${supportTicketId}/messages/${messageId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
}

function uploadSupportTicketMessageFiles({
  files,
  supportTicketId,
  messageId
}: {
  files: EditablePdf[];
  supportTicketId: number;
  messageId: number;
}) {
  const formData = new FormData();
  files.forEach(f => {
    if (f.file) {
      formData.append('files[]', f.file);
    }
  });
  return axios.patch<Message>(`/v1/support_tickets/${supportTicketId}/messages/${messageId}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
}

export function createSupportTicketMessageEmoji({
  supportTicketId,
  messageId,
  value
}: {
  supportTicketId: number;
  messageId: number;
  value: string;
}) {
  return axios.post<Message>(`/v1/support_tickets/${supportTicketId}/message_emojis`, {
    message_id: messageId,
    value
  });
}

export function destroySupportTicketMessageEmoji({
  supportTicketId,
  messageEmojiId
}: {
  supportTicketId: number;
  messageEmojiId: number;
}) {
  return axios.delete<Message>(`/v1/support_tickets/${supportTicketId}/message_emojis/${messageEmojiId}`);
}

interface CreateSupportRequestParams {
  description: string;
  images: EditableImage[];
  files: EditablePdf[];
}

export const createSupportRequest = async ({ description, images, files }: CreateSupportRequestParams) => {
  const formData = new FormData();
  formData.append('description', description);
  if (images.length) {
    images.forEach(image => {
      if (image.file) {
        formData.append('images[]', image.file);
      }
    });
  }
  if (files.length) {
    files.forEach(file => {
      if (file.file) {
        formData.append('files[]', file.file);
      }
    });
  }

  return await axios.post<SupportTicket>('/v1/support_tickets', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};

export const approveWorkOrder = async ({
  supportTicketId,
  workOrderId
}: {
  supportTicketId: number;
  workOrderId: number;
}) => {
  return axios.post<WorkOrder>(`/v1/support_tickets/${supportTicketId}/work_orders/${workOrderId}/approve`);
};

export const addWorkOrderFeedback = async ({
  supportTicketId,
  workOrderId,
  liked
}: {
  supportTicketId: number;
  workOrderId: number;
  liked: boolean;
}) => {
  return axios.post<WorkOrder>(`/v1/support_tickets/${supportTicketId}/work_orders/${workOrderId}/add_feedback`, {
    liked
  });
};

export const addSupportTicketFeedback = async ({
  supportTicketId,
  liked,
  message
}: {
  supportTicketId: number;
  liked: boolean;
  message?: string;
}) => {
  return axios.post<SupportTicket>(`/v1/support_tickets/${supportTicketId}/feedback`, { liked, message });
};

export function getBuildingUsers({ buildingId }: { buildingId: number }) {
  return axios.get<BuildingUser[]>(`/v2/buildings/${buildingId}/building_users`);
}

export function createSupportTicketAssignees({
  supportTicketId,
  userIds
}: {
  supportTicketId: number;
  userIds: number[];
}) {
  return axios.post<SupportTicket>(`/v1/support_tickets/${supportTicketId}/assignees`, {
    user_ids: userIds
  });
}

export function getTenantEmployeeRequests({ tenancyId, status }: { tenancyId: number; status?: 'open' | 'closed' }) {
  const params = status ? { status } : {};
  return axios.get<TenantEmployeeRequest[]>(`/v2/tenancies/${tenancyId}/tenant_employee_requests`, { params });
}

export function getTenantEmployeeRequest({ tenancyId, requestId }: { tenancyId: number; requestId: number }) {
  return axios.get<TenantEmployeeRequest>(`/v2/tenancies/${tenancyId}/tenant_employee_requests/${requestId}`);
}

export function getHasSlackInstallation(tenancyId: number) {
  return axios.get<{ has_slack_installation: boolean; app_home_deep_link?: string | null }>(
    `/v2/tenancies/${tenancyId}/slack_installation`
  );
}

export function convertTenantEmployeeRequestToSupportTicket({
  tenancyId,
  requestId
}: {
  tenancyId: number;
  requestId: number;
}) {
  return axios.post<SupportTicket>(
    `/v2/tenancies/${tenancyId}/tenant_employee_requests/${requestId}/convert_to_support_ticket`
  );
}

export function rejectTenantEmployeeRequest({ tenancyId, requestId }: { tenancyId: number; requestId: number }) {
  return axios.post<TenantEmployeeRequest>(`/v2/tenancies/${tenancyId}/tenant_employee_requests/${requestId}/reject`);
}
